import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Newspaper } from "lucide-react";
import { format } from "date-fns";

const NewsSection = () => {
  const { language } = useLanguage();

  const { data: articles = [] } = useQuery({
    queryKey: ["homepage-news"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles" as any)
        .select("id, title, title_ar, title_fr, slug, short_description, short_description_ar, short_description_fr, featured_image, category, tags, published_at")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false })
        .limit(4);
      if (error) throw error;
      return data as any[];
    },
  });

  if (articles.length === 0) return null;

  const getTitle = (a: any) =>
    language === "ar" ? (a.title_ar || a.title) : language === "fr" ? (a.title_fr || a.title) : a.title;
  const getDesc = (a: any) =>
    language === "ar"
      ? (a.short_description_ar || a.short_description)
      : language === "fr"
        ? (a.short_description_fr || a.short_description)
        : a.short_description;

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/40 to-background" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/[0.03] blur-[120px]" />

      <div className="synra-container relative z-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-primary" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                {language === "ar" ? "اخبار" : language === "fr" ? "Actualites" : "News"}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
              {language === "ar" ? "آخر الأخبار والمقالات" : language === "fr" ? "Dernieres Actualites" : "Latest News & Articles"}
            </h2>
            <p className="text-muted-foreground mt-2 max-w-lg">
              {language === "ar"
                ? "تابع آخر التطورات في عالم الذكاء الاصطناعي والأتمتة"
                : language === "fr"
                  ? "Suivez les dernieres evolutions en IA et automatisation"
                  : "Stay updated with the latest in AI and automation"}
            </p>
          </div>
          <Link
            to="/news"
            className="group inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {language === "ar" ? "جميع الأخبار" : language === "fr" ? "Toutes les actualites" : "View all articles"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </Link>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Featured Article — Large Card */}
          <Link to={`/news/${featured.slug}`} className="group relative block">
            <article className="relative h-full min-h-[420px] rounded-2xl overflow-hidden border border-border/50 bg-card">
              {/* Image */}
              {featured.featured_image ? (
                <img
                  src={featured.featured_image}
                  alt={getTitle(featured)}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-card flex items-center justify-center">
                  <Newspaper className="h-16 w-16 text-primary/30" />
                </div>
              )}
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full">
                    {featured.category}
                  </span>
                  {featured.published_at && (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(featured.published_at), "MMM dd, yyyy")}
                    </span>
                  )}
                </div>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                  {getTitle(featured)}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2 max-w-md">
                  {getDesc(featured)}
                </p>
              </div>
            </article>
          </Link>

          {/* Side Stack — Compact Cards */}
          <div className="flex flex-col gap-5">
            {rest.map((article: any) => (
              <Link to={`/news/${article.slug}`} key={article.id} className="group block">
                <article className="flex gap-4 p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm hover:border-primary/30 hover:bg-card transition-all duration-300">
                  {/* Thumbnail */}
                  {article.featured_image ? (
                    <img
                      src={article.featured_image}
                      alt={getTitle(article)}
                      className="w-28 h-24 md:w-32 md:h-[100px] rounded-lg object-cover flex-shrink-0 transition-transform duration-300 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-28 h-24 md:w-32 md:h-[100px] rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Newspaper className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  {/* Text */}
                  <div className="flex flex-col justify-between py-0.5 min-w-0">
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary/80">
                          {article.category}
                        </span>
                      </div>
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-2">
                        {getTitle(article)}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-2">
                      <Calendar className="h-3 w-3" />
                      {article.published_at ? format(new Date(article.published_at), "MMM dd, yyyy") : ""}
                    </div>
                  </div>
                </article>
              </Link>
            ))}

            {/* CTA mini card */}
            <Link
              to="/news"
              className="group flex items-center justify-center gap-3 p-5 rounded-xl border border-dashed border-primary/20 hover:border-primary/40 bg-primary/[0.03] hover:bg-primary/[0.06] transition-all duration-300"
            >
              <span className="text-sm font-medium text-primary">
                {language === "ar" ? "اكتشف المزيد من الأخبار" : language === "fr" ? "Decouvrir plus d'actualites" : "Discover more articles"}
              </span>
              <ArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
